import { Modal, useMediaDevice } from '@bob/library'
import { AppProviderContext } from '@websrc/App/AppContext'
import { matomo } from '@websrc/App/Tracking/Matomo'
import { mapExitPopup } from '@websrc/DataMappers/mapExitPopup'
import React, { FC, useCallback, useContext, useEffect, useState } from 'react'

import { Asset } from '../Asset/Asset'
import { CTA } from '../CTA'
import Heading from '../Heading/Heading'
import { Markdown } from '../Markdown/Markdown'
import { exitPopup } from './ExitPopup.css'

export const ExitPopup: FC<ReturnType<typeof mapExitPopup>> = ({
  headline,
  asset,
  cta,
  description,
}) => {
  const device = useMediaDevice()
  const [show, setShow] = useState(false)
  const { matomoPageTemplate } = useContext(AppProviderContext)

  const trackPopupDismissed = useCallback(() => {
    matomo.trackEvent(
      'conversion',
      matomoPageTemplate,
      ['page exit popup'],
      [`exit popup dismissed ${headline}`]
    )
  }, [headline, matomoPageTemplate])

  const trackPopupShown = useCallback(() => {
    matomo.trackEvent(
      'conversion',
      matomoPageTemplate,
      ['page exit popup'],
      [`exit popup shown ${headline}`]
    )
  }, [headline, matomoPageTemplate])

  const handleDismissPopup = useCallback(() => {
    sessionStorage.setItem('exitPopupDismissed', 'true')
    setShow(false)
    trackPopupDismissed()
  }, [trackPopupDismissed])

  useEffect(() => {
    const handleShowPopup = (event: MouseEvent) => {
      const sessionStorageStatus = sessionStorage.getItem('exitPopupDismissed')
      if (!show && sessionStorageStatus !== 'true' && event.clientY <= 0) {
        setShow(true)
        trackPopupShown()
      }
    }

    document.documentElement.addEventListener('mouseleave', handleShowPopup)

    return () => {
      document.documentElement.removeEventListener('mouseleave', handleShowPopup)
    }
  }, [show, trackPopupShown])

  return (
    <>
      {/* preload of the image, so the popup doesn't appear without on slow network */}
      {!!asset && (
        <div
          style={{
            opacity: 0,
            position: 'absolute',
            top: '-9999px',
            left: '-9999px',
            width: '1px',
            height: '1px',
          }}
        >
          <Asset thumbnail={asset.thumbnail} />
        </div>
      )}
      {show && (
        <Modal
          className={exitPopup._}
          closeCallback={() => handleDismissPopup()}
          overlayBackgroundColor={'dark'}
          position={'center'}
          style={{ padding: '0px', margin: '0px' }}
        >
          <div className={exitPopup.wrapper}>
            {device !== 'desktop' ? (
              <>
                <h1 className={exitPopup.headline}>{headline}</h1>
                {!!asset && (
                  <Asset
                    className={exitPopup.image}
                    objectFit={'cover'}
                    thumbnail={asset.thumbnail}
                  />
                )}
                {!!description && (
                  <Markdown className={exitPopup.description} markdown={description} />
                )}
                {!!cta && (
                  <CTA
                    className={exitPopup.cta}
                    colorTheme={'blue'}
                    cta={cta}
                    onClick={() => {
                      matomo.trackEvent(
                        'conversion',
                        matomoPageTemplate,
                        ['page exit popup cta'],
                        [`exit popup cta ${cta.text}`],
                        'button'
                      )
                    }}
                    type={'primary'}
                  />
                )}
              </>
            ) : (
              <>
                {!!asset && (
                  <Asset
                    className={exitPopup.image}
                    objectFit={'cover'}
                    thumbnail={asset.thumbnail}
                  />
                )}
                <div className={exitPopup.contentWrapper}>
                  <Heading description={description} headline={headline} level={2} size={'xl'} />
                  {!!cta && (
                    <CTA className={exitPopup.cta} colorTheme={'blue'} cta={cta} type={'primary'} />
                  )}
                </div>
              </>
            )}
          </div>
        </Modal>
      )}
    </>
  )
}
